import { createAsyncThunk } from '@reduxjs/toolkit';
import { t } from 'i18next';

import type { LeaveQueueProperties } from 'features/leave-queue/interfaces';
import { setInnerLoading } from 'features/app/store/app/appSlice';
import { LogoutType } from 'features/call/call-base/enums';
import { callerSessionAnalyticsInfo } from 'features/caller-session/helpers';
import { stoppedTakingCalls } from 'features/caller-session/store';
import { leaveQueueAnalytics } from 'features/leave-queue/helpers';
import { handleError, showNotification } from 'features/notification/store';
import { finishSession } from 'features/session/store';

export const leaveQueue = createAsyncThunk(
  'leaveQueue/leaveQueue',
  async ({ shouldFinishSession }: LeaveQueueProperties, { dispatch }) => {
    dispatch(setInnerLoading(true));
    dispatch(stoppedTakingCalls());
    dispatch(callerSessionAnalyticsInfo('Stopped taking calls'));

    const logoutType = LogoutType.Normal;

    try {
      dispatch(leaveQueueAnalytics('leaveQueue', LogoutType[logoutType]));

      if (shouldFinishSession) {
        await dispatch(finishSession({ logoutType })).unwrap();
      }
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'leaveQueue',
        })
      );
    } finally {
      dispatch(setInnerLoading(false));
      window.location.reload();
    }
  }
);

export const leaveQueueBecauseOfConnectionFailure = createAsyncThunk(
  'leaveQueue/leaveQueueBecauseOfConnectionFailure',
  async (
    { isRnsConnectionClosed }: { isRnsConnectionClosed: boolean },
    { dispatch }
  ) => {
    dispatch(setInnerLoading(true));
    const logoutType = LogoutType.ConnectionLoss;
    dispatch(stoppedTakingCalls());
    dispatch(callerSessionAnalyticsInfo('Stopped taking calls'));

    try {
      dispatch(
        leaveQueueAnalytics(
          isRnsConnectionClosed
            ? 'leaveQueueBecauseOfRnsConnectionFailed'
            : 'leaveQueueBecauseOfRegistrationFailed',
          LogoutType[logoutType]
        )
      );

      dispatch(
        showNotification({
          severity: 'error',
          title: t('somethingWentWrong'),
          message: isRnsConnectionClosed
            ? t('connectionIssue')
            : t('registrationFailed'),
        })
      );

      await dispatch(finishSession({ logoutType })).unwrap();
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: isRnsConnectionClosed
            ? 'leaveQueueBecauseOfRnsConnectionFailed'
            : 'leaveQueueBecauseOfRegistrationFailed',
        })
      );
    } finally {
      dispatch(setInnerLoading(false));
    }
  }
);
